import {
  CUSTOMER_DASHBOARD_MOBILE_PAYLOAD_KEY,
  CUSTOMER_DASHBOARD_PAYLOAD_KEY,
  CustomerDashboardLayoutDevice,
} from './customer-dashboard-layout.util';
import {
  ProductsPageBannerConfig,
  ProductsPageBannerModeKey,
  PRODUCTS_PAGE_BANNER_MOBILE_MODE_DRAFTS_PAYLOAD_KEY,
  PRODUCTS_PAGE_BANNER_MODE_DRAFTS_PAYLOAD_KEY,
  PRODUCTS_PAGE_BANNER_MOBILE_PAYLOAD_KEY,
  PRODUCTS_PAGE_BANNER_PAYLOAD_KEY,
  parseProductsPageBannerForDevice,
  parseProductsPageBannerModeDrafts,
  productsPageBannerModeDraftsToPayload,
  productsPageBannerToPayloadRecord,
} from './products-page-layout.util';

const STRAY_PRODUCTS_PAGE_BANNER_ROOT_KEYS = [
  PRODUCTS_PAGE_BANNER_PAYLOAD_KEY,
  PRODUCTS_PAGE_BANNER_MOBILE_PAYLOAD_KEY,
  PRODUCTS_PAGE_BANNER_MODE_DRAFTS_PAYLOAD_KEY,
  PRODUCTS_PAGE_BANNER_MOBILE_MODE_DRAFTS_PAYLOAD_KEY,
] as const;

/** Dashboard layouts store banner under `customerDashboard`; ignore stray products-page root keys. */
function shouldParseBannerFromCustomerDashboardRoots(
  payload: Record<string, unknown> | null | undefined,
): boolean {
  if (!payload || typeof payload !== 'object') {
    return false;
  }
  return (
    Object.prototype.hasOwnProperty.call(payload, CUSTOMER_DASHBOARD_PAYLOAD_KEY) ||
    Object.prototype.hasOwnProperty.call(payload, CUSTOMER_DASHBOARD_MOBILE_PAYLOAD_KEY)
  );
}

/** Remove products-page banner keys mistakenly written onto a dashboard layout document. */
export function sanitizeCustomerDashboardLayoutPayload(
  payload: Record<string, unknown>,
): Record<string, unknown> {
  const next = { ...payload };
  for (const key of STRAY_PRODUCTS_PAGE_BANNER_ROOT_KEYS) {
    delete next[key];
  }
  return next;
}

export const CUSTOMER_DASHBOARD_BANNER_PAYLOAD_KEY = 'banner';
export const CUSTOMER_DASHBOARD_BANNER_MODE_DRAFTS_PAYLOAD_KEY = 'bannerModeDrafts';

function readDashboardRoot(
  payload: Record<string, unknown> | null | undefined,
  device: CustomerDashboardLayoutDevice,
): Record<string, unknown> | null {
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  if (device === 'desktop') {
    const root = payload[CUSTOMER_DASHBOARD_PAYLOAD_KEY];
    return root && typeof root === 'object' && !Array.isArray(root)
      ? (root as Record<string, unknown>)
      : null;
  }
  const mobile = payload[CUSTOMER_DASHBOARD_MOBILE_PAYLOAD_KEY];
  if (mobile && typeof mobile === 'object' && !Array.isArray(mobile)) {
    return mobile as Record<string, unknown>;
  }
  return null;
}

/** Map dashboard roots to the products-page parser shape. Mobile uses only its own banner. */
export function syntheticProductsPageBannerPayload(
  fullPayload: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const desktopRoot = readDashboardRoot(fullPayload, 'desktop');
  const mobileRoot = readDashboardRoot(fullPayload, 'mobile');
  const synthetic: Record<string, unknown> = {
    [PRODUCTS_PAGE_BANNER_PAYLOAD_KEY]: desktopRoot?.[CUSTOMER_DASHBOARD_BANNER_PAYLOAD_KEY],
  };
  if (
    mobileRoot &&
    Object.prototype.hasOwnProperty.call(mobileRoot, CUSTOMER_DASHBOARD_BANNER_PAYLOAD_KEY)
  ) {
    synthetic[PRODUCTS_PAGE_BANNER_MOBILE_PAYLOAD_KEY] =
      mobileRoot[CUSTOMER_DASHBOARD_BANNER_PAYLOAD_KEY];
  }
  return synthetic;
}

export function parseCustomerDashboardBannerForDevice(
  payload: Record<string, unknown> | null | undefined,
  device: CustomerDashboardLayoutDevice,
): ProductsPageBannerConfig {
  if (shouldParseBannerFromCustomerDashboardRoots(payload)) {
    return parseProductsPageBannerForDevice(syntheticProductsPageBannerPayload(payload), device);
  }
  if (payload && Object.prototype.hasOwnProperty.call(payload, PRODUCTS_PAGE_BANNER_PAYLOAD_KEY)) {
    return parseProductsPageBannerForDevice(payload, device);
  }
  if (payload && Object.prototype.hasOwnProperty.call(payload, CUSTOMER_DASHBOARD_BANNER_PAYLOAD_KEY)) {
    if (device === 'mobile') {
      return parseProductsPageBannerForDevice(undefined, 'mobile');
    }
    return parseProductsPageBannerForDevice(
      { [PRODUCTS_PAGE_BANNER_PAYLOAD_KEY]: payload[CUSTOMER_DASHBOARD_BANNER_PAYLOAD_KEY] },
      'desktop',
    );
  }
  return parseProductsPageBannerForDevice(syntheticProductsPageBannerPayload(payload), device);
}

export function parseCustomerDashboardBannerModeDrafts(
  payload: Record<string, unknown> | null | undefined,
  device: CustomerDashboardLayoutDevice,
  activeBanner: ProductsPageBannerConfig,
): Record<ProductsPageBannerModeKey, ProductsPageBannerConfig> {
  if (shouldParseBannerFromCustomerDashboardRoots(payload)) {
    const root = readDashboardRoot(payload, device);
    const synthetic = {
      [PRODUCTS_PAGE_BANNER_MODE_DRAFTS_PAYLOAD_KEY]:
        root?.[CUSTOMER_DASHBOARD_BANNER_MODE_DRAFTS_PAYLOAD_KEY],
    };
    return parseProductsPageBannerModeDrafts(synthetic, 'desktop', activeBanner);
  }
  if (payload && Object.prototype.hasOwnProperty.call(payload, PRODUCTS_PAGE_BANNER_MODE_DRAFTS_PAYLOAD_KEY)) {
    return parseProductsPageBannerModeDrafts(payload, device, activeBanner);
  }
  const root = readDashboardRoot(payload, device);
  const synthetic = {
    [PRODUCTS_PAGE_BANNER_MODE_DRAFTS_PAYLOAD_KEY]:
      root?.[CUSTOMER_DASHBOARD_BANNER_MODE_DRAFTS_PAYLOAD_KEY],
  };
  return parseProductsPageBannerModeDrafts(synthetic, 'desktop', activeBanner);
}

export function customerDashboardBannerFieldsFromConfig(
  config: ProductsPageBannerConfig,
  drafts: Record<ProductsPageBannerModeKey, ProductsPageBannerConfig>,
): Record<string, unknown> {
  return {
    [CUSTOMER_DASHBOARD_BANNER_PAYLOAD_KEY]: productsPageBannerToPayloadRecord(config, {
      deviceImageConfigured: true,
    }),
    [CUSTOMER_DASHBOARD_BANNER_MODE_DRAFTS_PAYLOAD_KEY]:
      productsPageBannerModeDraftsToPayload(drafts),
  };
}
